import { useState } from "react";
import { Search, X, Compass, Lock, UserPlus, Check, Clock, Umbrella, Crown, Palmtree } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import { AdeLoadingSpinner } from "./AdeLoadingSpinner";
import { useLang } from "@/i18n";

/**
 * IslandSearch — Exact name search only (Section 12)
 * Sender sees ONLY island name until connected (Section 5-6)
 * No directory, no fuzzy search, no personal data exposed
 */
export function IslandSearch({ onIslandSelect, onClose }) {
  const { t } = useLang();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim() || searchTerm.length < 3) {
      toast.error("Inserisci almeno 3 caratteri");
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await api.get(`/islands/search`, { params: { name: searchTerm.trim() } });
      setResults(response.data?.islands || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Errore nella ricerca");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (island) => {
    try {
      await api.post(`/connections/request/${island.user_id}`);
      toast.success("Richiesta inviata!");
      // Refresh
      handleSearch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="rounded-3xl overflow-hidden border border-white/10" style={{ background: "linear-gradient(180deg, rgba(10,20,32,0.97), rgba(4,10,18,0.99))", backdropFilter: "blur(18px)" }} data-testid="island-search-modal">
      {/* Header */}
      <div className="p-4 text-white border-b border-white/8" style={{ background: "radial-gradient(120% 120% at 0% 0%, rgba(127,231,255,0.16), transparent 62%)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-cyan-200" style={{ background: "rgba(127,231,255,0.12)", border: "1px solid rgba(127,231,255,0.3)" }}>
              <Compass className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-base font-black uppercase tracking-[0.16em]" style={{ background: "linear-gradient(100deg,#fff,#7FE7FF)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{t('searchIsland')}</h2>
              <p className="text-[11px] text-white/50">Inserisci il nome esatto dell'isola</p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Search Input */}
      <div className="p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Es: Aurora47"
              className="w-full pl-10 pr-4 py-3 rounded-2xl text-white bg-white/5 border border-white/10 outline-none focus:border-cyan-300/50 focus:bg-white/[0.07] transition-all placeholder:text-white/30"
              autoFocus
              maxLength={30}
              data-testid="island-search-input"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || searchTerm.length < 3}
            className="px-5 py-3 rounded-2xl font-black text-[11px] uppercase tracking-[0.14em] text-cyan-100 disabled:opacity-40 transition-transform active:scale-[0.97]" style={{ background: "rgba(127,231,255,0.12)", border: "1px solid rgba(127,231,255,0.32)" }}
            data-testid="island-search-submit-btn"
          >
            {loading ? <AdeLoadingSpinner size="sm" /> : t('search')}
          </button>
        </div>
        <p className="text-[10.5px] text-white/40 mt-2.5 text-center flex items-center justify-center gap-1">
          <Lock className="w-3 h-3" /> Solo ricerca esatta — nessun dato personale esposto
        </p>
      </div>

      {/* Results */}
      <div className="max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8"><AdeLoadingSpinner size="md" /></div>
        ) : results.length > 0 ? (
          <div className="p-4 pt-0 space-y-2">
            {results.map((island) => (
              <div
                key={island.user_id}
                className="p-4 rounded-2xl border border-white/10 bg-white/[0.04]"
                data-testid={`island-result-${island.island_name}`}
              >
                <div className="flex items-center gap-3">
                  {/* Island icon (no personal photo unless connected) */}
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-cyan-200 shrink-0" style={{ background: "rgba(127,231,255,0.1)", border: "1px solid rgba(127,231,255,0.28)" }}>
                    {island.island_type === "island_premium_villa" ? (
                      <Umbrella className="w-6 h-6" strokeWidth={2.2} />
                    ) : island.island_type === "island_ultra_premium" ? (
                      <Crown className="w-6 h-6" strokeWidth={2.2} />
                    ) : (
                      <Palmtree className="w-6 h-6" strokeWidth={2.2} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white">{island.island_name}</p>

                    {island.is_self ? (
                      <p className="text-xs text-pink-500 font-medium">La tua isola</p>
                    ) : island.is_connected ? (
                      <p className="text-xs text-green-600 font-medium">{island.owner_name || 'Collegato'}</p>
                    ) : (
                      /* Section 5: Sender sees NO identity until connected */
                      <p className="text-xs text-white/40 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Identita nascosta
                      </p>
                    )}
                  </div>

                  {/* Action */}
                  {island.is_self ? (
                    <button
                      onClick={() => onIslandSelect(island)}
                      className="text-xs text-cyan-600 font-semibold px-3 py-1.5 bg-cyan-100 rounded-full"
                    >
                      {t('visit')}
                    </button>
                  ) : island.is_connected ? (
                    <button
                      onClick={() => onIslandSelect(island)}
                      className="text-xs text-green-600 font-semibold px-3 py-1.5 bg-green-100 rounded-full"
                    >
                      {t('visit')}
                    </button>
                  ) : island.has_pending_request ? (
                    <span className="text-xs text-amber-500 font-medium px-3 py-1.5 bg-amber-50 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" /> In attesa
                    </span>
                  ) : (
                    <button
                      onClick={() => handleRequest(island)}
                      className="text-xs text-white font-semibold px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center gap-1 shadow-md"
                      data-testid="island-request-btn"
                    >
                      <UserPlus className="w-3 h-3" /> Collegati
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : hasSearched ? (
          <div className="text-center py-8 text-white/40">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/5 flex items-center justify-center">
              <Search className="w-5 h-5 text-gray-300" />
            </div>
            <p className="text-sm">{t('noIslandFound')}</p>
            <p className="text-xs mt-1">Verifica il nome esatto dell'isola</p>
          </div>
        ) : (
          <div className="text-center py-8 text-white/40">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-400/10 flex items-center justify-center">
              <Compass className="w-5 h-5 text-cyan-400" />
            </div>
            <p className="text-sm">Inserisci il nome esatto di un'isola</p>
            <p className="text-xs mt-1">Chiedi il nome all'utente che vuoi trovare</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default IslandSearch;
